/**
 * TOSS Web Application - PTO Progress Timeline Component
 *
 * Visual timeline of PTO statuses with interactive tooltips.
 * Displays ring progress indicators showing inspection completion percentage.
 * Reusable across ApplicationCard, KanbanCard, ApplicationTable.
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ApplicationPtoStatus, ApplicationPtoStatusLabels } from '@/types';
import type { ApplicationPtoTimelineDto } from '@/api/applications.api';

// ============================================================================
// Types
// ============================================================================

export type PtoTimelineVariant = 'dots' | 'timeline' | 'stepper';
export type PtoTimelineSize = 'xs' | 'sm' | 'md';

export interface PtoProgressTimelineProps {
  /** Array of PTO timeline data */
  ptos: ApplicationPtoTimelineDto[];
  /** Display variant: dots (compact) or timeline (with connectors) */
  variant?: PtoTimelineVariant;
  /** Display size */
  size?: PtoTimelineSize;
  /** Show label with examined/total count */
  showLabel?: boolean;
  /** Maximum PTOs to show before "+N" badge (for compact views) */
  maxVisible?: number;
  /** Disable tooltips (useful for drag operations) */
  disableTooltips?: boolean;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

/** Ring diameter in pixels */
const ringSizes: Record<PtoTimelineSize, number> = {
  xs: 12,
  sm: 16,
  md: 20,
};

/** Ring stroke width in pixels */
const strokeWidths: Record<PtoTimelineSize, number> = {
  xs: 2,
  sm: 2,
  md: 3,
};

/** Status colors for the progress ring */
const statusColors: Record<ApplicationPtoStatus, string> = {
  [ApplicationPtoStatus.Completed]: '#22c55e',        // green-500
  [ApplicationPtoStatus.PartiallyCompleted]: '#f59e0b', // amber-500
  [ApplicationPtoStatus.NotCompleted]: '#ef4444',     // red-500
  [ApplicationPtoStatus.NotExamined]: '#9ca3af',      // gray-400
};

const connectorWidths: Record<PtoTimelineSize, string> = {
  xs: 'w-1',
  sm: 'w-1.5',
  md: 'w-2',
};

/** Connector color reflecting how "done" the transition out of a PTO is */
function getConnectorColor(status: ApplicationPtoStatus): string {
  if (status === ApplicationPtoStatus.Completed) return 'bg-[var(--color-success)]';
  if (status === ApplicationPtoStatus.PartiallyCompleted) return 'bg-[var(--color-warning-subdued)]';
  return 'bg-[var(--color-border)]';
}

// ============================================================================
// Ring Progress Component
// ============================================================================

interface PtoRingProgressProps {
  pto: ApplicationPtoTimelineDto;
  size: PtoTimelineSize;
}

function PtoRingProgress({ pto, size }: PtoRingProgressProps): JSX.Element {
  const diameter = ringSizes[size];
  const strokeWidth = strokeWidths[size];
  const radius = (diameter - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  // Calculate progress: inspectedCount / itemsCount
  const progress = pto.itemsCount > 0
    ? pto.inspectedCount / pto.itemsCount
    : 0;
  const strokeDashoffset = circumference * (1 - progress);

  const color = statusColors[pto.status];
  const isNotExamined = pto.status === ApplicationPtoStatus.NotExamined;

  return (
    <svg
      width={diameter}
      height={diameter}
      className={`flex-shrink-0 ${isNotExamined ? 'animate-pulse' : ''}`}
    >
      {/* Background circle */}
      <circle
        cx={diameter / 2}
        cy={diameter / 2}
        r={radius}
        fill="none"
        stroke="#e5e7eb"
        strokeWidth={strokeWidth}
      />
      {/* Progress circle */}
      <circle
        cx={diameter / 2}
        cy={diameter / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        transform={`rotate(-90 ${diameter / 2} ${diameter / 2})`}
        style={{ transition: 'stroke-dashoffset 0.3s ease-in-out' }}
      />
    </svg>
  );
}

// ============================================================================
// Ring + Tooltip (shared by timeline & stepper variants)
// ============================================================================

interface PtoRingTooltipProps {
  pto: ApplicationPtoTimelineDto;
  size: PtoTimelineSize;
  disableTooltip?: boolean;
}

function PtoRingTooltip({ pto, size, disableTooltip = false }: PtoRingTooltipProps): JSX.Element {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const ringRef = useRef<HTMLDivElement>(null);

  // Update tooltip position when showing
  useEffect(() => {
    if (showTooltip && ringRef.current) {
      const rect = ringRef.current.getBoundingClientRect();
      setTooltipPos({
        top: rect.top - 8, // 8px above the ring
        left: rect.left + rect.width / 2,
      });
    }
  }, [showTooltip]);

  const statusLabel = ApplicationPtoStatusLabels[pto.status] ?? pto.status;

  // Calculate progress percentage for tooltip
  const progressPercent = pto.itemsCount > 0
    ? Math.round((pto.inspectedCount / pto.itemsCount) * 100)
    : 0;

  return (
    <>
      <div
        ref={ringRef}
        className={`flex-shrink-0 ${!disableTooltip ? 'hover:scale-125 cursor-pointer transition-transform' : ''}`}
        onMouseEnter={() => !disableTooltip && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <PtoRingProgress pto={pto} size={size} />
      </div>

      {/* Tooltip via Portal */}
      {showTooltip && !disableTooltip && createPortal(
        <div
          className="
            fixed -translate-x-1/2 -translate-y-full
            min-w-[160px] max-w-[220px]
            px-3 py-2 rounded-lg
            bg-[var(--color-text-primary)] text-white text-xs
            shadow-[var(--shadow-overlay)] pointer-events-none
          "
          style={{
            top: tooltipPos.top,
            left: tooltipPos.left,
            zIndex: 9999,
          }}
        >
          <div className="font-medium text-sm mb-1">{pto.ptoName}</div>
          <div className="text-[var(--color-text-disabled)]">{statusLabel}</div>
          <div className="text-[var(--color-text-disabled)] mt-1">
            Механик: {pto.mechanicName || <span className="text-[var(--color-warning)]">Не назначен</span>}
          </div>
          <div className="text-[var(--color-text-disabled)] mt-1">
            Осмотрено: {pto.inspectedCount}/{pto.itemsCount} ({progressPercent}%)
          </div>
          {/* Arrow */}
          <div
            className="
              absolute top-full left-1/2 -translate-x-1/2
              border-4 border-transparent border-t-[var(--color-text-primary)]
            "
          />
        </div>,
        document.body
      )}
    </>
  );
}

// ============================================================================
// Timeline Item Component (Ring + Connector)
// ============================================================================

interface PtoTimelineItemProps {
  pto: ApplicationPtoTimelineDto;
  isLast: boolean;
  size: PtoTimelineSize;
  showConnector: boolean;
  disableTooltip?: boolean;
}

function PtoTimelineItem({
  pto,
  isLast,
  size,
  showConnector,
  disableTooltip = false,
}: PtoTimelineItemProps): JSX.Element {
  return (
    <div className="relative flex items-center">
      <PtoRingTooltip pto={pto} size={size} disableTooltip={disableTooltip} />
      {showConnector && !isLast && (
        <div className={`${connectorWidths[size]} h-0.5 ${getConnectorColor(pto.status)}`} />
      )}
    </div>
  );
}

// ============================================================================
// Stepper Item Component (Ring + label + side connectors)
// ============================================================================

interface PtoStepperItemProps {
  pto: ApplicationPtoTimelineDto;
  index: number;
  isLast: boolean;
  size: PtoTimelineSize;
  disableTooltip?: boolean;
}

function PtoStepperItem({
  pto,
  index,
  isLast,
  size,
  disableTooltip = false,
}: PtoStepperItemProps): JSX.Element {
  // Non-last steps grow to fill width (connector stretches to the next ring);
  // the last step shrinks to content so the row stays left-anchored.
  return (
    <div className={`flex min-w-0 flex-col items-start gap-1 ${isLast ? '' : 'flex-1'}`}>
      <div className="flex w-full items-center">
        <PtoRingTooltip pto={pto} size={size} disableTooltip={disableTooltip} />
        {!isLast && (
          <div className={`h-0.5 flex-1 ${getConnectorColor(pto.status)}`} />
        )}
      </div>
      <span className="max-w-full truncate text-3xs leading-tight text-[var(--color-text-secondary)]">
        ПТО-{index + 1}
      </span>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * PTO Progress Timeline displaying inspection status for each PTO
 */
export function PtoProgressTimeline({
  ptos,
  variant = 'timeline',
  size = 'sm',
  showLabel = true,
  maxVisible,
  disableTooltips = false,
  className = '',
}: PtoProgressTimelineProps): JSX.Element | null {
  if (!ptos || ptos.length === 0) return null;

  // Calculate totals - count PTOs that have been examined (not NotExamined)
  const examinedPtos = ptos.filter(
    (pto) => pto.status !== ApplicationPtoStatus.NotExamined
  ).length;

  // Determine which PTOs to show
  const visiblePtos = maxVisible && ptos.length > maxVisible
    ? ptos.slice(0, maxVisible)
    : ptos;
  const hiddenCount = maxVisible ? Math.max(0, ptos.length - maxVisible) : 0;

  const showConnector = variant === 'timeline';

  // Stepper variant: labeled horizontal steps (used in application cards)
  if (variant === 'stepper') {
    const lastVisibleStatus = visiblePtos[visiblePtos.length - 1]?.status;
    return (
      <div className={`space-y-1.5 ${className}`}>
        {showLabel && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--color-text-secondary)]">ПТО:</span>
            <span className="text-xs text-[var(--color-text-secondary)]">
              {examinedPtos}/{ptos.length}
            </span>
          </div>
        )}

        <div className="flex items-start">
          {visiblePtos.map((pto, index) => (
            <PtoStepperItem
              key={pto.id}
              pto={pto}
              index={index}
              isLast={index === visiblePtos.length - 1 && hiddenCount === 0}
              size={size}
              disableTooltip={disableTooltips}
            />
          ))}

          {/* Hidden count step */}
          {hiddenCount > 0 && (
            <div className="flex flex-1 min-w-0 flex-col items-center gap-1">
              <div className="flex w-full items-center">
                <div
                  className={`h-0.5 flex-1 ${
                    lastVisibleStatus ? getConnectorColor(lastVisibleStatus) : 'bg-[var(--color-border)]'
                  }`}
                />
                <span className="flex-shrink-0 rounded bg-[var(--color-bg-tertiary)] px-1.5 py-0.5 text-3xs font-medium text-[var(--color-text-secondary)]">
                  +{hiddenCount}
                </span>
              </div>
              <span className="text-3xs leading-tight text-[var(--color-text-secondary)]">ещё</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-1.5 ${className}`}>
      {/* Label */}
      {showLabel && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-[var(--color-text-secondary)]">ПТО:</span>
          <span className="text-xs text-[var(--color-text-secondary)]">
            {examinedPtos}/{ptos.length}
          </span>
        </div>
      )}

      {/* Timeline rings */}
      <div className="flex items-center gap-0.5">
        {visiblePtos.map((pto, index) => (
          <PtoTimelineItem
            key={pto.id}
            pto={pto}
            isLast={index === visiblePtos.length - 1 && hiddenCount === 0}
            size={size}
            showConnector={showConnector}
            disableTooltip={disableTooltips}
          />
        ))}

        {/* Hidden count badge */}
        {hiddenCount > 0 && (
          <span className="ml-1 px-1.5 py-0.5 text-3xs font-medium text-[var(--color-text-secondary)] bg-[var(--color-bg-tertiary)] rounded">
            +{hiddenCount}
          </span>
        )}
      </div>
    </div>
  );
}

export default PtoProgressTimeline;
